import { del, get, set } from 'idb-keyval';
import { useState } from 'react';
import { useBoolean, useEffectOnce } from 'usehooks-ts';

import { getOfflineStore } from 'common/offline/reactQueryPersistence';

export const useIndexedDb = <A>(key: string) => {
  const [value, setValue] = useState<undefined | A>(undefined);
  const { value: isLoading, setValue: setIsLoading } = useBoolean(true);
  const { value: isError, setValue: setIsError } = useBoolean(false);

  useEffectOnce(() => {
    const handleIdbUpdate = () => {
      setIsLoading(true);
      getOfflineStore()
        .then((store) => get<A>(key, store))
        .then((v) => {
          setValue(v);
        })
        .catch(() => {
          setIsError(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    handleIdbUpdate();

    window.addEventListener('idbUpdate', handleIdbUpdate);

    return () => window.removeEventListener('idbUpdate', handleIdbUpdate);
  });

  const setIdbValue = (v: A) => {
    return getOfflineStore()
      .then((store) => set(key, v, store))
      .then(() => {
        const idbUpdateCustomEvent = new CustomEvent('idbUpdate');
        window.dispatchEvent(idbUpdateCustomEvent);
      })
      .catch(() => {
        setIsError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const delIdbValue = () => {
    setIsLoading(true);
    getOfflineStore()
      .then((store) => del(key, store))
      .then(() => {
        setIsLoading(false);
      })
      .catch(() => {
        setIsError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return {
    value,
    remove: delIdbValue,
    set: setIdbValue,
    isLoading,
    isError,
  };
};
